import React, { type PropsWithChildren } from 'react'

import { Box, cn } from '@hanzo/ui'
import type { LinkDef } from '@hanzo/ui/types'

import AuthWidget from '../auth/widget'
import Logo, { type LogoVariant } from '../logo'
import DesktopBagPopup from '../commerce/desktop-bag-popup'
import BagButton from '../commerce/bag-button'
import DesktopNav from './desktop-nav-menu'
import { anchor } from '../../util/anchor'

const DesktopHeader: React.FC<{
  currentAs: string | undefined
  links: LinkDef[]
  /** Right of the nav: the site's own calls to action, `variant: 'primary'` as the filled pill. */
  actions?: LinkDef[]
  className?: string
  noAuth?: boolean
  noCommerce?: boolean
  logoVariant?: LogoVariant
} & PropsWithChildren> = ({
  links,
  actions = [],
  className = '',
  noAuth = false,
  noCommerce = false,
  children,
  logoVariant = 'wordmark',
}) => (
  <Box
    tag='header'
    id='DESKTOP_HEADER'
    className={cn(
      'fixed top-0 left-0 right-0 z-[300] h-12',
      'bg-black/80 backdrop-blur-xl border-b border-white/[0.08]',
      className
    )}
  >
    <Box className='w-full max-w-[1024px] mx-auto px-6 h-full flex flex-row items-center'>
      <Logo size='sm' href='/' variant={logoVariant} />

      <Box className='flex-1 flex flex-row justify-center'>
        <DesktopNav links={links} />
      </Box>

      <Box className='flex flex-row items-center gap-5'>
        {actions.map((a) => <Action key={a.href} {...a} />)}
        {!noAuth && <AuthWidget />}

        {!noCommerce && (
          <DesktopBagPopup popupClx='w-[340px]' trigger={<BagButton className='text-primary -mr-[3px] lg:min-w-0' />} />
        )}
        {children}
      </Box>
    </Box>
  </Box>
)

export default DesktopHeader

export const Action: React.FC<LinkDef> = (l) => {
  const { href, title, variant } = l
  return (
    <Box
      tag='a'
      href={href}
      {...anchor(l)}
      className={cn(
        'no-underline text-xs leading-none transition duration-150',
        variant === 'primary'
          ? 'inline-flex items-center h-7 px-3.5 rounded-full font-medium bg-white text-black hover:bg-neutral-200'
          : 'text-neutral-300 hover:text-white'
      )}
    >
      {title}
    </Box>
  )
}
